import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAppContext } from '../context/AppContext';
import DatePicker from "react-datepicker";
import { FaPlusCircle, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';

import "react-datepicker/dist/react-datepicker.css";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.spacing.large};
  color: ${props => props.theme.colors.text};
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.large};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${props => props.theme.spacing.medium};
`;

const FormGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  align-items: center;
  gap: ${props => props.theme.spacing.large};
`;

const Label = styled.label`
  font-weight: bold;
  text-align: right;
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.small};
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  width: 96%;
  height: 38px; // Match the height of react-select
`;

const StyledDatePicker = styled(DatePicker)`
  padding: ${props => props.theme.spacing.small};
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  width: 96%;
  height: 38px; // Match the height of react-select
`;

const RadioGroup = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
`;

const ButtonContainer = styled.div`
  grid-column: 2;
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.large};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.medium};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  width: 100%;

  &:hover {
    opacity: 0.8;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0;
  margin-left: ${props => props.theme.spacing.small};
`;

const ErrorText = styled.span`
  color: #ff6b6b;
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
`;

const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 40px; // Make room for the eye icon
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  height: 100%;
  
  &:hover {
    opacity: 0.7;
  }
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
    borderColor: 'rgb(71, 71, 71)',
    borderRadius: '20px',
    padding: '0.25rem',
    color: 'white',
    width: '100%',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FDBA31' : 'rgb(71, 71, 71)',
    color: state.isSelected ? 'black' : 'white',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
  }),
};

const useCoachForm = (initialCoach) => {
  const [coach, setCoach] = useState(initialCoach);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleCoachInfoChange = useCallback((value, field) => {
    setCoach(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleConfirmPasswordChange = useCallback((value) => {
    setConfirmPassword(value);
    // Clear confirm password error when user starts typing
    if (errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  }, [errors]);

  const validateForm = useCallback((isEditMode = false) => {
    const newErrors = {};

    // Required field validation
    if (!coach.username?.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!coach.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!coach.lastname?.trim()) {
      newErrors.lastname = 'Last name is required';
    }
    if (!coach.email?.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!coach.campus?.trim()) {
      newErrors.campus = 'Campus is required';
    }

    // Password validation: required for new coaches, optional for editing existing coaches
    const passwordProvided = coach.password?.trim();
    const confirmPasswordProvided = confirmPassword?.trim();
    
    if (!isEditMode) {
      // Creating new coach - password required
      if (!passwordProvided) {
        newErrors.password = 'Password is required';
      }
    }
    
    // If password is provided (new or edit), validate it
    if (passwordProvided) {
      if (coach.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      }
      
      // Password confirmation validation - only if password is provided
      if (coach.password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (confirmPasswordProvided) {
      // If confirm password is provided but main password isn't
      newErrors.password = 'Password is required when confirm password is provided';
    }

    // Email format validation
    if (coach.email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(coach.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // Age validation - coaches must be at least 16 years old
    if (coach.dob) {
      const today = new Date();
      const birthDate = new Date(coach.dob);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) 
        ? age - 1 
        : age;
      
      if (actualAge < 16) {
        newErrors.dob = 'Coach must be at least 16 years old';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [coach, confirmPassword]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  const resetForm = useCallback(() => {
    setCoach(initialCoach);
    setConfirmPassword('');
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [initialCoach]);

  return {
    coach,
    setCoach,
    confirmPassword,
    errors,
    showPassword,
    showConfirmPassword,
    handleCoachInfoChange,
    handleConfirmPasswordChange,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    validateForm,
    resetForm
  };
};

const CoachAdminPage = () => {
  const { 
    user,
    coachObjectList,
    campusList,
    flask_post,
    loadCoaches,
  } = useAppContext();

  const [editMode, setEditMode] = useState(true);
  
  const initialCoach = {
    username: '', 
    password: '', 
    role: 'coach', 
    firstName: '', 
    lastname: '', 
    email: '', 
    dob: new Date(), 
    campus: ''
  };

  const {
    coach: editCoach,
    setCoach: setEditCoach,
    confirmPassword: editConfirmPassword,
    errors: editErrors,
    showPassword: editShowPassword,
    showConfirmPassword: editShowConfirmPassword,
    handleCoachInfoChange: handleEditCoachChange,
    handleConfirmPasswordChange: handleEditConfirmPasswordChange,
    togglePasswordVisibility: toggleEditPasswordVisibility,
    toggleConfirmPasswordVisibility: toggleEditConfirmPasswordVisibility,
    validateForm: validateEditForm,
    resetForm: resetEditForm
  } = useCoachForm(initialCoach);

  const {
    coach: newCoach,
    confirmPassword: newConfirmPassword,
    errors: newErrors,
    showPassword: newShowPassword,
    showConfirmPassword: newShowConfirmPassword,
    handleCoachInfoChange: handleNewCoachChange,
    handleConfirmPasswordChange: handleNewConfirmPasswordChange,
    togglePasswordVisibility: toggleNewPasswordVisibility,
    toggleConfirmPasswordVisibility: toggleNewConfirmPasswordVisibility,
    validateForm: validateNewForm,
    resetForm: resetNewForm
  } = useCoachForm(initialCoach);

  // Determine current values based on mode
  const currentCoach = editMode ? editCoach : newCoach;
  const currentConfirmPassword = editMode ? editConfirmPassword : newConfirmPassword;
  const currentErrors = editMode ? editErrors : newErrors;
  const currentShowPassword = editMode ? editShowPassword : newShowPassword;
  const currentShowConfirmPassword = editMode ? editShowConfirmPassword : newShowConfirmPassword;
  const currentHandleCoachChange = editMode ? handleEditCoachChange : handleNewCoachChange;
  const currentHandleConfirmPasswordChange = editMode ? handleEditConfirmPasswordChange : handleNewConfirmPasswordChange;
  const currentTogglePasswordVisibility = editMode ? toggleEditPasswordVisibility : toggleNewPasswordVisibility;
  const currentToggleConfirmPasswordVisibility = editMode ? toggleEditConfirmPasswordVisibility : toggleNewConfirmPasswordVisibility;
  const currentValidateForm = editMode ? validateEditForm : validateNewForm;

  const handleCoachDelete = useCallback(async (coachToDelete) => {
    try {
      const result = await Swal.fire({
        title: 'Confirm coach deletion?',
        text: 'This action is not reversible',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        confirmButtonColor: "#FDBA31",
      });

      if (result.isConfirmed) {
        const response = await flask_post('DeleteCoach', [coachToDelete]);
        
        if (response.data.success) {
          await loadCoaches();
          document.getElementById("coachAdminForm").reset();
          resetEditForm();
          Swal.fire('Deleted!', 'The coach has been deleted.', 'success');
        } else {
          Swal.fire('Error', `Failed to delete coach: ${response.data.error}`, 'error');
        }
      }
    } catch (error) {
      console.error('Delete coach error:', error);
      Swal.fire('Error', 'Failed to delete coach', 'error');
    }
  }, [flask_post, resetEditForm, loadCoaches]);

  useEffect(() => {
    if (user && coachObjectList && coachObjectList.length > 0) {
      // Only auto-load current user's profile if no coach is currently selected
      if (!editCoach.username) {
        const currentCoach = coachObjectList.find(coach => coach.username === user.username);
        if (currentCoach) {
          // Clear password field when loading coach data for security
          const coachWithoutPassword = { ...currentCoach, password: '' };
          setEditCoach(coachWithoutPassword);
        }
      }
    }
  }, [user, coachObjectList, setEditCoach, editCoach.username]);

  const handleCreateNewCoach = useCallback(() => {
    setEditMode(false);
    resetNewForm();
  }, [resetNewForm]);

  const handleCoachSelection = useCallback((selected) => {
    const selectedCoach = coachObjectList.find(coach => coach.username === selected.value);
    if (selectedCoach) {
      // Clear password fields when switching coaches for security
      const coachWithoutPassword = { ...selectedCoach, password: '' };
      setEditCoach(coachWithoutPassword);
      // Also clear the confirm password field
      handleEditConfirmPasswordChange('');
      setEditMode(true);
    }
  }, [coachObjectList, setEditCoach, handleEditConfirmPasswordChange]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!currentValidateForm(editMode)) {
      Swal.fire('Error', 'Please fix the validation errors before submitting', 'error');
      return;
    }

    try {
      // Prepare coach data for submission
      const coachDataToSubmit = { ...currentCoach };
      
      // If editing and password is empty, don't send password (keep existing password)
      if (editMode && !currentCoach.password?.trim()) {
        delete coachDataToSubmit.password;
      }
      
      const response = await flask_post(
        editMode ? 'EditCoach' : 'CreateCoach',
        [coachDataToSubmit]
      );
      
      if (response.data.success) {
        await loadCoaches();
        
        // Keep the updated coach selected (with cleared password for security)
        if (editMode) {
          const coachWithoutPassword = { ...currentCoach, password: '' };
          setEditCoach(coachWithoutPassword);
          // Also clear the confirm password field using the edit-specific function
          handleEditConfirmPasswordChange('');
        } else {
          // For new coach creation, clear the new form
          resetNewForm();
        }
        
        Swal.fire('Success', `Coach ${editMode ? 'updated' : 'created'} successfully`, 'success');
        
        if (!editMode) {
          setEditMode(true);
        }
        
      } else {
        Swal.fire('Error', `Failed to ${editMode ? 'update' : 'create'} coach: ${response.data.error}`, 'error');
      }
    } catch (error) {
      console.error('Coach submit error:', error);
      Swal.fire('Error', `Failed to ${editMode ? 'update' : 'create'} coach`, 'error');
    }
  }, [editMode, currentCoach, currentValidateForm, flask_post, loadCoaches, setEditMode, setEditCoach, handleEditConfirmPasswordChange, resetNewForm]);
  
  return (
    <PageContainer>
      <Title>{editMode ? 'Edit Coach Profile' : 'Create Coach Profile'}</Title>
      <Form onSubmit={handleSubmit} id="coachAdminForm">
        <FormGroup>
          <Label>User:</Label>
          <InputWrapper>
            {editMode ? (
              <Select
                options={coachObjectList?.map(coach => ({ value: coach.username, label: coach.username }))}
                value={currentCoach?.username ? { value: currentCoach.username, label: currentCoach.username } : null}
                onChange={handleCoachSelection}
                styles={customSelectStyles}
                placeholder="Select coach"
                isDisabled={user.role !== 'admin'}
              />
            ) : (
              <>
                <Input
                  type="text"
                  value={currentCoach?.username || ''}
                  onChange={(e) => currentHandleCoachChange(e.target.value, 'username')}
                  placeholder="username"
                />
                {currentErrors.username && <ErrorText>{currentErrors.username}</ErrorText>}
              </>
            )}
            {editMode && user.role === 'admin' && (
              <>
                <IconButton type="button" onClick={handleCreateNewCoach}>
                  <FaPlusCircle />
                </IconButton>
                {currentCoach?.username && currentCoach.username !== user.username && (
                  <IconButton type="button" onClick={() => handleCoachDelete(currentCoach)}>
                    <FaTrash />
                  </IconButton>
                )}
              </>
            )}
          </InputWrapper>
        </FormGroup>

        {user.role === 'admin' && currentCoach?.username !== user.username && (
          <FormGroup>
            <Label>Role:</Label>
            <RadioGroup>
              <label>
                <input
                  type="radio"
                  value="coach"
                  name="role"
                  checked={currentCoach?.role === 'coach'}
                  onChange={(event) => currentHandleCoachChange(event.target.value, 'role')}
                />
                Coach
              </label>
              <label>
                <input
                  type="radio"
                  value="admin"
                  name="role"
                  checked={currentCoach?.role === 'admin'}
                  onChange={(event) => currentHandleCoachChange(event.target.value, 'role')}
                />
                Admin
              </label>
            </RadioGroup>
          </FormGroup>
        )}

        <FormGroup>
          <Label>Campus:</Label>
          <InputWrapper>
            <Select
              options={campusList.map(campus => ({ value: campus, label: campus }))}
              value={currentCoach?.campus ? { value: currentCoach.campus, label: currentCoach.campus } : null}
              onChange={(selected) => currentHandleCoachChange(selected.value, 'campus')}
              styles={customSelectStyles}
              placeholder="Select campus"
            />
            {currentErrors.campus && <ErrorText>{currentErrors.campus}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>First name:</Label>
          <InputWrapper>
            <Input
              type="text"
              value={currentCoach?.firstName || ''}
              onChange={(e) => currentHandleCoachChange(e.target.value, 'firstName')}
              placeholder="firstname"
            />
            {currentErrors.firstName && <ErrorText>{currentErrors.firstName}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Last name:</Label>
          <InputWrapper>
            <Input
              type="text"
              value={currentCoach?.lastname || ''}
              onChange={(e) => currentHandleCoachChange(e.target.value, 'lastname')}
              placeholder="lastname"
            />
            {currentErrors.lastname && <ErrorText>{currentErrors.lastname}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Email:</Label>
          <InputWrapper>
            <Input
              type="email"
              value={currentCoach?.email || ''}
              onChange={(e) => currentHandleCoachChange(e.target.value, 'email')}
              placeholder="email"
            />
            {currentErrors.email && <ErrorText>{currentErrors.email}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Password:</Label>
          <InputWrapper>
            <PasswordInputContainer>
              <PasswordInput
                type={currentShowPassword ? "text" : "password"}
                value={currentCoach?.password || ''}
                onChange={(e) => currentHandleCoachChange(e.target.value, 'password')}
                placeholder={editMode ? "password (leave blank to keep current)" : "password"}
              />
              <PasswordToggleButton
                type="button"
                onClick={currentTogglePasswordVisibility}
              >
                {currentShowPassword ? <FaEyeSlash /> : <FaEye />}
              </PasswordToggleButton>
            </PasswordInputContainer>
            {currentErrors.password && <ErrorText>{currentErrors.password}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Confirm Password:</Label>
          <InputWrapper>
            <PasswordInputContainer>
              <PasswordInput
                type={currentShowConfirmPassword ? "text" : "password"}
                value={currentConfirmPassword || ''}
                onChange={(e) => currentHandleConfirmPasswordChange(e.target.value)}
                placeholder={editMode ? "confirm password (if changing)" : "confirm password"}
              />
              <PasswordToggleButton
                type="button"
                onClick={currentToggleConfirmPasswordVisibility}
              >
                {currentShowConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </PasswordToggleButton>
            </PasswordInputContainer>
            {currentErrors.confirmPassword && <ErrorText>{currentErrors.confirmPassword}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>DOB:</Label>
          <InputWrapper>
            <StyledDatePicker
              selected={currentCoach?.dob ? new Date(currentCoach.dob) : new Date()}
              onChange={(date) => currentHandleCoachChange(date, 'dob')}
              dateFormat="dd/MM/yyyy"
              placeholderText="date of birth"
            />
            {currentErrors.dob && <ErrorText>{currentErrors.dob}</ErrorText>}
          </InputWrapper>
        </FormGroup>

        <ButtonContainer>
          <Button type="submit">
            {editMode ? "Update" : "Create"}
          </Button>
        </ButtonContainer>
      </Form>
    </PageContainer>
  );
}

export default CoachAdminPage;